import { Component, Inject } from "@angular/core";
import { CulturalActivity } from "@common/ADAPT.Common.Model/organisation/cultural-activity";
import { CulturalActivityType } from "@common/ADAPT.Common.Model/organisation/cultural-activity-type";
import { SpeedCatchup } from "@common/ADAPT.Common.Model/organisation/speed-catchup";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseDialogWithDiscardConfirmationComponent } from "@common/ux/adapt-common-dialog/base-dialog-with-discard-confirmation.component/base-dialog-with-discard-confirmation.component";
import { DeleteActivityDialogComponent } from "@org-common/lib/activities/delete-activity-dialog/delete-activity-dialog.component";
import { CulturalActivityMetadataByType } from "@org-common/lib/cultural-activity/cultural-activity-type-metadata";
import moment from "moment";
import { switchMap } from "rxjs";
import { tap } from "rxjs/operators";

@Component({
    selector: "adapt-reschedule-activity-dialog",
    templateUrl: "./reschedule-activity-dialog.component.html",
    standalone: false,
})
export class RescheduleActivityDialogComponent extends BaseDialogWithDiscardConfirmationComponent<CulturalActivity> {
    public readonly dialogName = "RescheduleActivityDialog";
    public readonly CulturalActivityType = CulturalActivityType;

    protected readonly autoResolveData = this.activity;
    public entitiesToConfirm: IBreezeEntity[];

    public readonly today = moment()
        .startOf("day")
        .toDate();

    public temporaryPcu?: SpeedCatchup;
    public selectedTeamIsValid = true;

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public activity: CulturalActivity,
        private dialogService: AdaptCommonDialogService,
    ) {
        super();

        this.entitiesToConfirm = [activity];
        if (activity.supplementaryData) {
            this.entitiesToConfirm.push(activity.supplementaryData);
        }

        // passed in activity date was in the past (and this is a new activity)
        if (moment(this.activity.date).isBefore(this.today) && this.isAdded) {
            this.activity.date = this.today;
        }

        // need to create a fake PCU for the team selector to work as expected
        if (activity.type === CulturalActivityType.PeerCatchUp && activity.person1Id && activity.person2Id) {
            this.temporaryPcu = new SpeedCatchup();
            this.temporaryPcu.person1 = activity.person1;
            this.temporaryPcu.person2 = activity.person2;
            this.temporaryPcu.team = activity.team;
        }
    }

    public get isAdded() {
        return this.activity.entityAspect.entityState.isAdded();
    }

    public get culturalActivityTypeMetadata() {
        return CulturalActivityMetadataByType[this.activity.type];
    }

    public get isInvalidOrNoChanges() {
        return (this.activity.entityAspect.entityState.isUnchanged() && !this.activity.supplementaryData?.comment)
            || !this.selectedTeamIsValid;
    }

    public get showDeleteButton() {
        return !this.isAdded && this.activity.type === CulturalActivityType.PeerCatchUp;
    }

    @Autobind
    public deleteActivity() {
        return this.dialogService.open(DeleteActivityDialogComponent, this.activity).pipe(
            tap(() => this.resolve(this.activity)),
        );
    }

    @Autobind
    public saveAndClose() {
        // remove suppData if empty
        const suppData = this.activity.supplementaryData ?? undefined;
        return this.commonDataService.remove(suppData?.comment ? undefined : suppData).pipe(
            tap(() => {
                if (this.isAdded) {
                    // newly scheduled event, it shouldn't have an initial date
                    // TODO: decide if we want to keep the original suggested date as the initial date
                    this.activity.initialDate = this.activity.date;
                }
            }),
            switchMap(() => this.commonDataService.saveEntities(this.entitiesToConfirm)),
            tap(() => this.resolve(this.activity)),
        );
    }

    public cancel() {
        // allow cancelling without prompting for discard if there is no comment
        // but only if the activity hasn't been changed as well, otherwise cancelling then "stay here" will remove the comment box!
        if (!this.activity.entityAspect.entityState.isAddedModifiedOrDeleted()
            && this.activity.supplementaryData
            && this.activity.supplementaryData.entityAspect.entityState.isAdded()
            && !this.activity.supplementaryData.comment) {
            this.activity.supplementaryData.entityAspect.rejectChanges();
        }

        return super.cancel();
    }
}
